import { useEffect, useRef, useState } from "react";
import { Button, ExpandableText, Tags } from "../../../ui";
import { CheckCircle, XCircle } from "../../../ui/icons";
import { isBranchAction } from "../../../../utils/branchWorkflow";
import BranchQuickActions, { branchStage } from "./BranchQuickActions";
import BranchWorkflow from "./BranchWorkflow";
import { MenuEmailDetailsButton, isMenuEmail } from "./MenuEmailActions";
import RequestFiles from "./RequestFiles";
import RowActions from "./RowActions";
import { actorText, appName, formatDate, formatDateTime, kindText } from "./requestHelpers";

// One request, understandable and actionable from this single row: title with its
// application (or branch) tag, a short summary, the status only when it means
// something, and - while in progress - the actions of that request type.
// A completed request shows only its final status, the date of that transition and,
// if it was rejected, the reason.
const CONTRACT_STAGE = { pending: "awaitingLud", inProgress: "awaitingLud", awaitingSignature: "awaitingSignature", awaitingContractReview: "awaitingReview" };

function summaryOf(item, t, lang) {
  const row = item.row || {};
  if (row.menuEmail) return t.menuEmail.summary(row.menuEmail.itemAction, row.menuEmail.itemCount, row.menuEmail.subject);
  if (row.closure?.from) return t.row.closure(formatDate(row.closure.from, lang) || row.closure.from, formatDate(row.closure.to, lang) || row.closure.to);
  if (row.action === "contractRequest" && CONTRACT_STAGE[item.status?.native]) return t.row.stage[CONTRACT_STAGE[item.status.native]];
  return item.summary;
}

// Who the request is waiting for, from its actual workflow stage (not from who is looking):
// a contract waits for the restaurant between LUD's upload and the signed copy; every other
// open request waits for LUD (or the restaurant's / LUD's reviewers) to review it.
export const waitingOn = (item) => (item.row?.action === "contractRequest" && item.status?.native === "awaitingSignature" ? "store" : "lud");

// The colour of a final status: completed green, rejected red, canceled gray.
// Operations (campaign actions): the status tag follows the workflow step, not only the plain status.
const ACTION_STAGE_VARIANT = { storeReview: "warning", ludReview: "info", inProgress: "primary", activated: "success", completed: "success", rejected: "danger", canceled: "neutral", deleted: "neutral" };
export const actionStageOf = (item) => (item.identity?.source === "operations" ? item.status?.stage || ({ rejected: "rejected", canceled: "canceled", deleted: "deleted", completed: "completed" })[item.status?.native] || null : null);

const FINAL_VARIANT = { completed: "success", approved: "success", rejected: "danger", canceled: "neutral", sent: "success" };
const MENU_EMAIL_VARIANT = { draft: "neutral", failed: "danger" };

export default function RequestRow({ item, t, lang, branches, done, role, highlighted = false, onChanged, onOpenFile }) {
  const [appsOpen, setAppsOpen] = useState(false);
  // a request the user was just sent here for: bring it into view once (and keep it marked)
  const ref = useRef(null);
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  }, [highlighted]);
  const row = item.row || {};
  const branchFlow = isBranchAction(item.kind?.code);
  const title = kindText(item, lang);

  // the tag next to the title: the branch for branch requests, the application otherwise
  let tag = "";
  if (branchFlow) {
    const branch = (branches || []).find((entry) => entry.id === item.scope?.branchId);
    tag = branch ? (lang === "ar" ? branch.nameAr : branch.nameEn || branch.nameAr) : "";
  } else tag = appName(item.scope?.application, lang);

  const final = row.final || (done ? { status: item.status?.display, at: item.updatedAt } : null);
  const rejected = done && ["rejected", "canceled"].includes(final?.status);
  const stage = branchFlow ? branchStage(item) : null;
  const summary = summaryOf(item, t, lang);
  const actionStage = actionStageOf(item);
  // an open request never shows an empty action area: it says who it is waiting for
  const waiting = waitingOn(item);
  const waitingTag = (
    <Tags size="sm" variant={waiting === "store" ? "warning" : "info"} appearance="soft" className="lud-ur-waiting">
      {waiting === "store" ? t.row.waitingStore : t.row.underReview}
    </Tags>
  );
  const hasPanelToggle = Boolean(stage) && (stage !== "submitted" || item.kind?.code === "updateBranchHours");
  const menuEmail = isMenuEmail(item);

  return (
    <li ref={ref} className={`lud-ur-row${done ? " is-done" : ""}${highlighted ? " is-highlighted" : ""}`} data-source={item.identity.source} aria-current={highlighted ? "true" : undefined}>
      {done && (
        <span className={`lud-ur-row-icon${rejected ? " is-negative" : ""}`} aria-hidden="true">
          {rejected ? <XCircle size={16} /> : <CheckCircle size={16} />}
        </span>
      )}
      <div className="lud-ur-row-main">
        <div className="lud-ur-row-title">
          <strong dir="auto">{title}</strong>
          {tag && <Tags size="sm" variant="neutral" appearance="soft">{tag}</Tags>}
          {menuEmail && !done && <Tags size="sm" variant={MENU_EMAIL_VARIANT[item.status?.native] || "neutral"} appearance="soft" className={`lud-ur-stage is-${item.status?.native}`}>{t.menuEmail.status[item.status?.native] || item.status?.label?.[lang === "ar" ? "ar" : "en"]}</Tags>}
          {actionStage && <Tags size="sm" variant={ACTION_STAGE_VARIANT[actionStage]} appearance="soft" className={`lud-ur-stage is-${actionStage}`}>{t.row.actionStage[actionStage]}</Tags>}
        </div>
        <ExpandableText className="lud-ur-row-summary" text={summary} lines={2} threshold={70} labels={t.expand} />
        {menuEmail && (
          <p className="lud-ur-row-meta">{t.menuEmail.created(formatDateTime(item.createdAt, lang), actorText(item.createdBy))}</p>
        )}
        {menuEmail && row.menuEmail?.error && <p className="lud-ur-row-reason" dir="auto">{t.menuEmail.fields.error}: {row.menuEmail.error}</p>}
        {!done && branchFlow && item.status?.label && <p className="lud-ur-row-state" dir="auto">{item.status.label[lang === "ar" ? "ar" : "en"]}</p>}
        {done && row.reason && <p className="lud-ur-row-reason" dir="auto">{t.row.reasonLabel}: {row.reason}</p>}
        <RequestFiles item={item} t={t} onOpenFile={onOpenFile} />
      </div>
      <div className="lud-ur-row-side">
        {done ? (
          <span className="lud-ur-row-final">
            <Tags size="sm" variant={FINAL_VARIANT[final?.status] || "neutral"} appearance="soft" className={`lud-ur-final is-${final?.status}`}>
              {t.row.final[final?.status] || item.status?.label?.[lang === "ar" ? "ar" : "en"]}{final?.override ? ` · ${t.row.withOverride}` : ""}
            </Tags>
            {final?.at && <time className="lud-ur-row-date">{formatDate(final.at, lang)}</time>}
            {menuEmail && <MenuEmailDetailsButton item={item} t={t} lang={lang} branches={branches} />}
          </span>
        ) : (
          <span className="lud-ur-quick" role="group" aria-label={title}>
            {branchFlow ? (
              <>
                <BranchQuickActions item={item} t={t} lang={lang} onDone={onChanged} onOpenDetails={() => setAppsOpen(true)} empty={hasPanelToggle ? null : waitingTag} />
                {hasPanelToggle && (
                  <Button size="sm" appearance="soft" variant="neutral" aria-expanded={appsOpen} onClick={() => setAppsOpen((value) => !value)}>
                    {appsOpen ? t.row.hideApplications : item.kind?.code === "updateBranchHours" && stage === "submitted" ? t.row.hours : t.row.applications}
                  </Button>
                )}
              </>
            ) : (
              <RowActions item={item} t={t} lang={lang} branches={branches} onChanged={onChanged} empty={waitingTag} />
            )}
          </span>
        )}
      </div>
      {!done && branchFlow && appsOpen && (
        <div className="lud-ur-row-panel">
          <BranchWorkflow request={item} t={t} lang={lang} onChanged={onChanged} />
        </div>
      )}
    </li>
  );
}
